// This file is part of MinIO Mission Control
// Copyright (c) 2023 MinIO, Inc.

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../app/store";
import {
  getNodeLabelsAsync,
  licenseAsync,
  loadGlobalSitesAsync,
} from "./systemThunks";
import { DateTime } from "luxon";
import { snackBarMessage } from "../app/types";
import { ErrorResponseHandler } from "../common/types";
import { MinioDeploymentDetails, NodeLabels } from "api/controlPlaneApi";
import { NavNamespace, NavSite } from "./system.types";
import { getMenuMemory, setMenuMemory } from "../common/utils";
import { MinioRPCError } from "../api/enterpriseRpcApi";

const initSideBarOpen = localStorage.getItem("sidebarOpen")
  ? JSON.parse(localStorage.getItem("sidebarOpen")!)["open"]
  : true;

export interface SystemState {
  license: string | null;
  loggedIn: boolean;
  sidebarOpen: boolean;
  status: "idle" | "loading" | "failed";
  licenseStatus: "neverChecked" | "idle" | "loading" | "failed";
  statusDeployments: "idle" | "loading" | "failed";
  statusSites: "idle" | "loading" | "failed" | "neverLoaded";
  deployments: MinioDeploymentDetails[];
  sites: NavSite[];
  selectedSite: string | null;
  deploymentDate: string;
  snackBar: snackBarMessage;
  modalSnackBar: snackBarMessage;
  helpName: string;
  helpTabName: string;
  menuExpandElements: string[];
  menuCollapsed: boolean;
  filter: string;
  nodeLabels: NodeLabels;
  loadingNodeLabels: boolean;
}

const initialState: SystemState = {
  license: null,
  loggedIn: false,
  sidebarOpen: initSideBarOpen,
  status: "idle",
  licenseStatus: "neverChecked",
  deployments: [],
  sites: [],
  selectedSite: localStorage.getItem("selectedSite"),
  statusDeployments: "idle",
  statusSites: "neverLoaded",
  deploymentDate: DateTime.now().toISO(),
  helpName: "help",
  helpTabName: "docs",
  snackBar: {
    message: "",
    detailedErrorMsg: "",
    type: "message",
  },
  modalSnackBar: {
    message: "",
    detailedErrorMsg: "",
    type: "message",
  },
  menuExpandElements: getMenuMemory(),
  menuCollapsed: false,
  filter: "",
  nodeLabels: {},
  loadingNodeLabels: false,
};

interface MenuExpandOption {
  menuElement: string;
  expanded: boolean;
}

export const systemSlice = createSlice({
  name: "system",
  initialState,
  reducers: {
    userLogged: (state, action: PayloadAction<boolean>) => {
      state.loggedIn = action.payload;
    },
    menuOpen: (state, action: PayloadAction<boolean>) => {
      // persist preference to local storage
      localStorage.setItem(
        "sidebarOpen",
        JSON.stringify({ open: action.payload }),
      );
      state.sidebarOpen = action.payload;
    },

    setSelectedDeploymentDate: (state, action: PayloadAction<string>) => {
      state.deploymentDate = action.payload;
    },
    setSnackBarMessage: (state, action: PayloadAction<string>) => {
      state.snackBar = {
        message: action.payload,
        detailedErrorMsg: "",
        type: "message",
      };
    },
    setModalSnackMessage: (state, action: PayloadAction<string>) => {
      state.modalSnackBar = {
        message: action.payload,
        detailedErrorMsg: "",
        type: "message",
      };
    },
    setModalErrorSnackMessage: (
      state,
      action: PayloadAction<{ errorMessage: string; detailedError: string }>,
    ) => {
      state.modalSnackBar = {
        message: action.payload.errorMessage,
        detailedErrorMsg: "",
        type: "message",
      };
    },
    setErrorSnackMessage: (
      state,
      action: PayloadAction<ErrorResponseHandler>,
    ) => {
      state.snackBar = {
        message: action.payload.errorMessage,
        detailedErrorMsg: action.payload.detailedError,
        type: "error",
      };
    },
    setErrorSnackBarMessage: (
      state,
      action: PayloadAction<ErrorResponseHandler | MinioRPCError>,
    ) => {
      state.snackBar = {
        message: `${action.payload.errorMessage}`,
        detailedErrorMsg: `${action.payload.detailedError}`,
        type: "error",
      };
    },
    setHelpName: (state, action: PayloadAction<string>) => {
      state.helpName = action.payload;
    },
    setHelpTabName: (state, action: PayloadAction<string>) => {
      state.helpTabName = action.payload;
    },
    setMenuStateItem: (state, action: PayloadAction<MenuExpandOption>) => {
      let menuMemory = [...state.menuExpandElements];

      if (action.payload.expanded) {
        menuMemory.push(action.payload.menuElement);
      } else {
        menuMemory = state.menuExpandElements.filter(
          (item: string) => item !== action.payload.menuElement,
        );
      }
      state.menuExpandElements = menuMemory;
      setMenuMemory(menuMemory);
    },
    selectSite: (state, action: PayloadAction<string>) => {
      localStorage.setItem("selectedSite", action.payload);
      state.selectedSite = action.payload;
    },
    collapseMenu: (state, action: PayloadAction<boolean>) => {
      state.menuCollapsed = action.payload;
    },
    setFilter: (state, action: PayloadAction<string>) => {
      state.filter = action.payload;
    },
    licenseChecked: (state) => {
      state.licenseStatus = "idle";
    },
    setLicense: (state, action: PayloadAction<string | null>) => {
      state.license = action.payload;
    },
    setNodeLabels: (state, action: PayloadAction<NodeLabels>) => {
      state.nodeLabels = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadGlobalSitesAsync.pending, (state) => {
        state.statusSites = "loading";
      })
      .addCase(loadGlobalSitesAsync.fulfilled, (state, action) => {
        state.statusSites = "idle";
        if (action.payload) {
          if (!state.selectedSite && action.payload.length > 0) {
            state.selectedSite = action.payload[0].id!;
          }
          // construct a cluster navigation
          let sites: NavSite[] = [];
          action.payload.forEach((site) => {
            let navCluster: NavSite = {
              site: site,
              namespaces: [],
            };
            let nsMap: {
              [id: string]: NavNamespace;
            } = {};
            site.resources?.tenants?.forEach((tenant) => {
              let ns: NavNamespace;
              if (!nsMap.hasOwnProperty(tenant.namespace!)) {
                ns = {
                  name: tenant.namespace!,
                  tenant: tenant,
                  observes: [],
                  catalogs: [],
                  keyManagers: [],
                  firewalls: [],
                };
                navCluster.namespaces.push(ns);
                nsMap[tenant.namespace!] = ns;
              } else {
                ns = nsMap[tenant.namespace!];
                ns.tenant = tenant;
              }
            });
            site.resources?.observes?.forEach((item) => {
              let ns: NavNamespace;
              if (!nsMap.hasOwnProperty(item.namespace!)) {
                ns = {
                  name: item.namespace!,
                  tenant: undefined,
                  observes: [item],
                  catalogs: [],
                  keyManagers: [],
                  firewalls: [],
                };
                navCluster.namespaces.push(ns);
                nsMap[item.namespace!] = ns;
              } else {
                ns = nsMap[item.namespace!];
                ns.observes.push(item);
              }
            });
            site.resources?.catalogs?.forEach((item) => {
              let ns: NavNamespace;
              if (!nsMap.hasOwnProperty(item.namespace!)) {
                ns = {
                  name: item.namespace!,
                  tenant: undefined,
                  observes: [],
                  catalogs: [item],
                  keyManagers: [],
                  firewalls: [],
                };
                navCluster.namespaces.push(ns);
                nsMap[item.namespace!] = ns;
              } else {
                ns = nsMap[item.namespace!];
                ns.catalogs.push(item);
              }
            });
            site.resources?.keyManagers?.forEach((item) => {
              let ns: NavNamespace;
              if (!nsMap.hasOwnProperty(item.namespace!)) {
                ns = {
                  name: item.namespace!,
                  tenant: undefined,
                  observes: [],
                  catalogs: [],
                  keyManagers: [item],
                  firewalls: [],
                };
                navCluster.namespaces.push(ns);
                nsMap[item.namespace!] = ns;
              } else {
                ns = nsMap[item.namespace!];
                ns.keyManagers.push(item);
              }
            });
            site.resources?.firewalls?.forEach((item) => {
              let ns: NavNamespace;
              if (!nsMap.hasOwnProperty(item.namespace!)) {
                ns = {
                  name: item.namespace!,
                  tenant: undefined,
                  observes: [],
                  catalogs: [],
                  keyManagers: [],
                  firewalls: [item],
                };
                navCluster.namespaces.push(ns);
                nsMap[item.namespace!] = ns;
              } else {
                ns = nsMap[item.namespace!];
                ns.firewalls.push(item);
              }
            });
            sites.push(navCluster);
          });

          sites.forEach((c) => {
            c.namespaces.sort((a, b) =>
              a.name ? a.name.localeCompare(b.name) : 0,
            );
          });

          state.sites = sites;
        }
      })
      .addCase(loadGlobalSitesAsync.rejected, (state) => {
        state.statusSites = "failed";
      })
      .addCase(licenseAsync.pending, (state) => {
        state.licenseStatus = "loading";
      })
      .addCase(licenseAsync.rejected, (state) => {
        state.licenseStatus = "failed";
      })
      .addCase(licenseAsync.fulfilled, (state, action) => {
        state.licenseStatus = "idle";
        state.license = `${action.payload!.valid_until!}`;
      })
      .addCase(getNodeLabelsAsync.pending, (state) => {
        state.loadingNodeLabels = true;
      })
      .addCase(getNodeLabelsAsync.rejected, (state) => {
        state.loadingNodeLabels = false;
      })
      .addCase(getNodeLabelsAsync.fulfilled, (state, action) => {
        state.loadingNodeLabels = false;
        state.nodeLabels = action.payload;
      });
  },
});

// Reducer actions
export const {
  collapseMenu,
  licenseChecked,
  menuOpen,
  selectSite,
  setErrorSnackBarMessage,
  setFilter,
  setHelpName,
  setHelpTabName,
  setLicense,
  setMenuStateItem,
  setNodeLabels,
  setSelectedDeploymentDate,
  setSnackBarMessage,
  userLogged,
  setModalSnackMessage,
  setModalErrorSnackMessage,
  setErrorSnackMessage,
} = systemSlice.actions;

// Reducer selectors
export const sidebarOpen = (state: RootState) => state.system.sidebarOpen;
export default systemSlice.reducer;
